import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Loader } from 'semantic-ui-react';
import { useGetProductsQuery, useGetQuizAttemptsQuery } from '../product_knowledge/productKnowledgeApiSlice';
import { useGetUsersQuery } from '../users/usersApiSlice';
import CustomDropdown from '../../components/CustomDropdown';
import { shortDate } from '../chat_sessions/MobileChatSessionList';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { NAV } from '../../navLabels';
import { ChipRow, DateRangeField, PhoneDateRange, SquareButton, Tag, cardAction, cardBody, cardHeader, cardMeta, cardTitle, listCard, mutedCell, ListContainer, ListPagination, PageBand, SearchField, SortTh, chip, firstCell, listPageStyle, pillButton, tableCard, td, th, cardStack } from '../../components/listPage';
import ScopeNote from '../../components/ScopeNote';

// 직원 관리 and an attempt's 이 직원의 퀴즈 기록 link here with ?trainee= (components/ScopeNote).
const LINKED_FILTERS = [['trainee', '선택한 직원의 퀴즈 기록']];

/**
 * 퀴즈 기록: quiz attempts, laid out like 롤플레이 기록 — a table on desktop, cards with infinite
 * scroll on phones, each opening the attempt's own page. Trainees see their own attempts (the
 * API scopes them); admins see everyone in scope and can filter by person.
 */

const PAGE_SIZE = 20;
const SCROLL_KEY = 'quizattempts:scroll';
const RESULTS = [['전체', null], ['정답', 'correct'], ['오답', 'incorrect']];
// Toggles: tap the active one again to show both types (no 전체 chip, so the row fits a phone).
const TYPES = [['객관식', 'MC'], ['서술형', 'OE']];
export const typeLabel = code => (code === 'OE' ? '서술형' : '객관식');

const clamp = lines => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-word',
});

const TypeTag = ({ code }) => <Tag tone="gray">{typeLabel(code)}</Tag>;
const ResultPill = ({ correct }) => <Tag tone={correct ? 'green' : 'red'}>{correct ? '정답' : '오답'}</Tag>;

// Same anatomy as the other list cards: title row (product, tags), meta (when, who), body.
const AttemptCard = ({ a, isAdmin, onOpen }) => (
  <div role="button" onClick={() => onOpen(a)} style={{ ...listCard, cursor: 'pointer' }}>
    <div style={cardHeader}>
      <div style={cardTitle}>{a.product_name || '퀴즈'}</div>
      <TypeTag code={a.question_type_code} />
      <ResultPill correct={a.is_correct} />
    </div>
    <div style={cardMeta}>
      {[shortDate(a.created_at), isAdmin ? a.user_display_name || a.user_username : null, isAdmin ? a.user_contractor : null].filter(Boolean).join(' · ')}
    </div>
    <div style={{ ...cardBody, ...clamp(2) }}>{a.question_text}</div>
  </div>
);

/** One page of the phone list; its own cached request, so restoring N pages is N cache hits. */
const Chunk = ({ page, params, onLoaded, renderCard }) => {
  const { data, isError } = useGetQuizAttemptsQuery({ ...params, page }, { refetchOnMountOrArgChange: 60 });
  useEffect(() => {
    if (data) onLoaded(page, !!data.next, data.count);
  }, [data, page, onLoaded]);
  if (isError) return <div style={{ textAlign: 'center', color: '#C0392B', padding: '12px' }}>불러오지 못했습니다.</div>;
  if (!data) return <Loader active inline="centered" size="small" style={{ margin: '16px auto' }} />;
  return data.results.map(renderCard);
};

const QuizAttemptsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const me = useSelector(state => state.auth.user);
  const isAdmin = !!(me?.is_staff || me?.is_superuser || me?.is_contractor_admin);

  const qs = new URLSearchParams(location.search);
  const get = k => qs.get(k);
  const page = Number(get('page') || 1);
  const loaded = Math.max(1, parseInt(get('loaded') || '1', 10) || 1);
  const [searchValue, setSearchValue] = useState(get('search') || '');
  const [datesOpen, setDatesOpen] = useState(!!(get('start_date') || get('end_date')));
  const [tail, setTail] = useState({ page: 0, hasNext: false, count: null });

  const params = Object.fromEntries(
    Object.entries({
      search: get('search'),
      status: get('status'),
      question_type: get('question_type'),
      product: get('product'),
      trainee: get('trainee'),
      start_date: get('start_date'),
      end_date: get('end_date'),
      ordering: get('ordering'),
      page_size: PAGE_SIZE,
    }).filter(([, v]) => v),
  );
  const filterKey = JSON.stringify(params);

  // Desktop: one page at a time. Phones render Chunks instead.
  const { data: desktopData, isFetching } = useGetQuizAttemptsQuery({ ...params, page }, { skip: isMobile });
  const { data: productsData } = useGetProductsQuery({ page_size: 1000, has_quiz: 'true' });
  const { data: usersData } = useGetUsersQuery({ page_size: 1000 }, { skip: !isAdmin });
  const productOptions = (productsData?.results || []).map(p => ({ key: p.id, text: p.name, value: p.id }));
  const userOptions = (usersData?.results || []).map(u => ({ key: u.id, text: u.full_name || u.username, value: u.id }));

  // keepPlace: only `loaded` / `page` changes — replace, don't add history.
  const setQuery = (changes, keepPlace = false) => {
    const p = new URLSearchParams(location.search);
    Object.entries(changes).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    if (!keepPlace) {
      p.delete('loaded');
      p.delete('page');
      window.scrollTo(0, 0);
    }
    navigate(`${location.pathname}?${p.toString()}`, { replace: keepPlace });
  };

  const onLoaded = useCallback((pg, hasNext, count) => {
    if (pg === loaded) setTail({ page: pg, hasNext, count });
  }, [loaded]);

  // Back from an attempt: restore the scroll offset once every loaded page is in.
  const restored = useRef(false);
  useEffect(() => {
    const ready = isMobile ? tail.page === loaded : !!desktopData;
    if (restored.current || !ready) return;
    restored.current = true;
    try {
      const saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
      if (saved && saved.key === location.search) window.scrollTo(0, saved.y);
      sessionStorage.removeItem(SCROLL_KEY);
    } catch (e) { /* private mode: start at the top */ }
  }, [tail.page, loaded, desktopData, isMobile, location.search]);

  const open = a => {
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ key: location.search, y: window.scrollY }));
    } catch (e) { /* private mode */ }
    navigate(`/quiz/attempts/${a.id}`);
  };

  const canLoadMore = tail.page === loaded && tail.hasNext;
  const loadMore = useCallback(() => {
    if (canLoadMore) setQuery({ loaded: String(loaded + 1) }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoadMore, loaded, location.search]);

  const sentinel = useRef(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !canLoadMore) return undefined;
    const io = new IntersectionObserver(([entry]) => entry.isIntersecting && loadMore(), { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [canLoadMore, loadMore]);

  const ordering = get('ordering');
  const sortBy = field => setQuery({ ordering: ordering === `-${field}` ? field : `-${field}` });
  const count = isMobile ? tail.count : desktopData?.count;
  const hasDates = !!(get('start_date') || get('end_date'));

  const resultChips = RESULTS.map(([label, value]) => (
    <button key={label} type="button" onClick={() => setQuery({ status: value })} style={chip((get('status') || null) === value)}>{label}</button>
  ));
  const typeChips = TYPES.map(([label, value]) => {
    const active = get('question_type') === value;
    return <button key={label} type="button" onClick={() => setQuery({ question_type: active ? null : value })} style={chip(active)}>{label}</button>;
  });
  // Phones: three chips + the count, like 롤플레이 기록's row; 유형 moves into the filter panel
  // (five chips and the count don't fit 362px and pushed the count to a second line).
  const chips = <ChipRow count={count} groups={isMobile ? [resultChips] : [resultChips, typeChips]} />;

  const searchBox = (
    <SearchField isMobile={isMobile} value={searchValue} onChange={setSearchValue} onSubmit={() => setQuery({ search: searchValue.trim() })} placeholder={isAdmin ? '문제, 제품, 직원 검색' : '문제, 제품 검색'} />
  );

  // Phones: two bordered inputs, as on 롤플레이 기록's phone filter. Desktop: one grey field
  // like the dropdowns and search beside it (and 롤플레이 기록's desktop date range).
  const dates = isMobile ? (
    <PhoneDateRange start={get('start_date')} end={get('end_date')} onChange={(k, v) => setQuery({ [k]: v })} onClear={() => setQuery({ start_date: null, end_date: null })} />
  ) : (
    <DateRangeField start={get('start_date')} end={get('end_date')} onChange={(k, v) => setQuery({ [k]: v })} onClear={() => setQuery({ start_date: null, end_date: null })} />
  );

  const dropdowns = (
    <>
      {/* Searchable: hundreds of products and staff. On phones they sit in a column, where a
          flex-basis would set their height — so a plain full width there. */}
      <CustomDropdown searchable placeholder="제품 선택" options={productOptions} value={get('product')} onChange={v => setQuery({ product: v })} icon="box" style={isMobile ? { width: '100%' } : { minWidth: '180px', flex: '0 0 auto' }} />
      {isAdmin && (
        <CustomDropdown searchable placeholder="직원 선택" options={userOptions} value={get('trainee')} onChange={v => setQuery({ trainee: v })} icon="user" style={isMobile ? { width: '100%' } : { minWidth: '160px', flex: '0 0 auto' }} />
      )}
    </>
  );

  return (
    <div style={listPageStyle(isMobile)}>
      <PageBand isMobile={isMobile} icon="history" title={NAV.QUIZ_HISTORY} subtitle={NAV.QUIZ_HISTORY_SUBTITLE} />

      <ListContainer isMobile={isMobile}>
        {isMobile ? (
          <>
            <div style={{ display: 'flex', gap: '8px' }}>
              {searchBox}
<SquareButton icon="sliders horizontal" label="필터" on={datesOpen} set={!!(hasDates || get('product') || get('trainee') || get('question_type'))} onClick={() => setDatesOpen(o => !o)} />
            </div>
            {datesOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                <div style={{ display: 'flex', gap: '6px' }}>{typeChips}</div>
                {dropdowns}
                {dates}
              </div>
            )}
            <ScopeNote linked={LINKED_FILTERS} />
            {chips}
            <div style={cardStack}>
              {Array.from({ length: loaded }, (_, i) => (
                <Chunk key={`${filterKey}:${i + 1}`} page={i + 1} params={params} onLoaded={onLoaded} renderCard={a => <AttemptCard key={a.id} a={a} isAdmin={isAdmin} onOpen={open} />} />
              ))}
            </div>
            {canLoadMore && (
              <div ref={sentinel} style={{ textAlign: 'center', marginTop: '16px' }}>
                <button type="button" onClick={loadMore} style={cardAction(false)}>더 보기</button>
              </div>
            )}
            {tail.page === loaded && !tail.hasNext && (
              <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#86868b' }}>
                {tail.count ? '모든 기록을 불러왔습니다.' : '퀴즈 기록이 없습니다.'}
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              {dates}
              {dropdowns}
              {searchBox}
              <button type="button" onClick={() => setQuery({ search: searchValue.trim() })} style={pillButton(true)}>
                검색
              </button>
            </div>
            <ScopeNote linked={LINKED_FILTERS} />
            {chips}
            <div style={{ ...tableCard, opacity: isFetching ? 0.6 : 1 }}>
              {!desktopData ? (
                <Loader active inline="centered" style={{ margin: '40px auto' }} />
              ) : desktopData.results.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#86868b' }}>퀴즈 기록이 없습니다.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <SortTh label="일시" field="created_at" ordering={ordering} onSort={sortBy} style={firstCell(th)} />
                        {isAdmin && <SortTh label="직원" field="user__username" ordering={ordering} onSort={sortBy} />}
                        <SortTh label="제품" field="question__product__name" ordering={ordering} onSort={sortBy} />
                        <th style={th}>문제</th>
                        <SortTh label="유형" field="question__question_type" ordering={ordering} onSort={sortBy} />
                        <SortTh label="결과" field="is_correct" ordering={ordering} onSort={sortBy} />
                      </tr>
                    </thead>
                    <tbody>
                      {desktopData.results.map(a => (
                        <tr key={a.id} onClick={() => open(a)} style={{ cursor: 'pointer' }}>
                          <td style={{ ...firstCell(td), ...mutedCell }}>{shortDate(a.created_at)}</td>
                          {isAdmin && (
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>
                              <div style={{ fontWeight: 600 }}>{a.user_display_name || a.user_username}</div>
                              {a.user_contractor && <div style={{ fontSize: '12px', color: '#86868b' }}>{a.user_contractor}</div>}
                            </td>
                          )}
                          <td style={{ ...td, fontWeight: 600, maxWidth: '200px' }}>{a.product_name || '—'}</td>
                          <td style={{ ...td, color: '#424245' }}><div style={clamp(2)}>{a.question_text}</div></td>
                          <td style={td}><TypeTag code={a.question_type_code} /></td>
                          <td style={td}><ResultPill correct={a.is_correct} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <ListPagination page={page} count={desktopData?.count || 0} pageSize={PAGE_SIZE} onChange={pg => { setQuery({ page: String(pg) }, true); window.scrollTo(0, 0); }} />
          </>
        )}
      </ListContainer>
    </div>
  );
};

export default QuizAttemptsList;
